// src/screens/MatchDetails.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LogoLoader } from "../components/LogoLoader";
import { supabase } from "../supabaseClient";
import SendReminderButton from "../components/SendReminderButton";
import {
  BarChart2,
  Dribbble,
  Pencil,
  PlusCircle,
  AlarmClock,
  Play,
  Trash2,
  Check,
  X,
  Home,
  Bus,
  FileDown,
  Users,
  CheckSquare,
  Square,
  Save,
  XCircle,
  Search as SearchIcon,
  ArrowLeftRight,
} from "lucide-react";
import { ExportButtons } from "../components/ExportButtons";
import i18n from "../i18n";
import jsPDF from "jspdf";
import Logo from "../assets/img/LogoSF.png";
import { BackButton } from "../components/BackButton";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileOpener } from "@awesome-cordova-plugins/file-opener";



/* ========= i18n mini-hook ========= */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null
      ? String((i18n.t as any)(key, options))
      : String((i18n.t as any)(key));
  return { t, lng };
}

type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
};

type MatchInfo = {
  opponent: string;
  date: string;
  location?: "domicile" | "extérieur";
  players?: Player[] | null; // joueurs convoqués (snapshot)
  score?: string;
  victory?: boolean;
  is_live_finished?: boolean;
  team_id?: string | null;
  team?: { id?: string; team_name: string; players?: Player[] | null } | null; // source joueurs
};

type ConvocationForm = {
  drivers: string;
  matchPlace: string;
  meetingTime: string;
  meetingPlace: string;
  gender: string; // "F" | "M" — accorde "Joueuses"/"Joueurs" dans le PDF
};

const CONVOCATION_GENDER_KEY = "swishdata_convocation_gender";

type PlayerStat = {
  id: string;
  player_number: number;
  player_name: string;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
  personal_fouls: number;
  is_opponent?: boolean; // <--- important pour séparer équipe / adversaires
};

export const MatchDetails = () => {
  const { t, lng } = useI18nSafe();
  const { id: matchId } = useParams();
  const navigate = useNavigate();

  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null);
  const [stats, setStats] = useState<PlayerStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<keyof PlayerStat | "eval" | null>(
    null
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [activeTab, setActiveTab] = useState<"stats" | "shots">("stats");
  const [shots, setShots] = useState<any[]>([]);
  const [selectedShooter, setSelectedShooter] = useState<string>("");
  const [isPremium, setIsPremium] = useState(false);

  // === Edition des convoqués ===
  const [isEditingPlayers, setIsEditingPlayers] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState<Player[]>([]);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [isConvocationModalOpen, setIsConvocationModalOpen] = useState(false);
  const [convocationForm, setConvocationForm] = useState<ConvocationForm>({
    drivers: "",
    matchPlace: "",
    meetingTime: "",
    meetingPlace: "",
    gender: "F",
  });
  const [extraPlayers, setExtraPlayers] = useState<
    { firstName: string; lastName: string }[]
  >([]);
  const [extraPlayerFirstName, setExtraPlayerFirstName] = useState("");
  const [extraPlayerLastName, setExtraPlayerLastName] = useState("");

  const editMatchLabel = lng === "fr" ? "Modifier le match" : "Edit match";
  const [isOwner, setIsOwner] = useState(false);

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  const calculateEFG = (
    fgm: number,
    fga: number,
    threeMade: number
  ): string => {
    if (fga === 0) return "–";
    return (((fgm + 0.5 * threeMade) / fga) * 100).toFixed(1) + " %";
  };

  const calculateTS = (points: number, fga: number, fta: number): string => {
    const denom = 2 * (fga + 0.44 * fta);
    if (denom === 0) return "–";
    return ((points / denom) * 100).toFixed(1) + " %";
  };

  const calculateEFF = (s: PlayerStat): number => {
    const missedFG = s.shots_attempted - s.shots_made;
    const missedFT = s.ft_attempted - s.ft_made;
    return (
      s.points +
      s.rebounds +
      s.assists +
      s.steals +
      s.blocks -
      s.turnovers -
      missedFG -
      missedFT
    );
  };

  const calculateTeamTS = (tstats: {
    points: number;
    shots_attempted: number;
    ft_attempted: number;
  }): string => {
    const denom = 2 * (tstats.shots_attempted + 0.44 * tstats.ft_attempted);
    if (denom === 0) return "–";
    return ((tstats.points / denom) * 100).toFixed(1) + " %";
  };

  const calculateTeamEFG = (tstats: {
    shots_made: number;
    shots_attempted: number;
    three_made: number;
  }): string => {
    if (tstats.shots_attempted === 0) return "–";
    return (
      ((tstats.shots_made + 0.5 * tstats.three_made) / tstats.shots_attempted) *
      100
    ).toFixed(1) + " %";
  };

  useEffect(() => {
    const checkPremium = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      setIsPremium(!!profile?.is_premium);
    };
    checkPremium();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!matchId) return;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return navigate("/login");

      const { data: matchStats } = await supabase
        .from("match_stats")
        .select("*")
        .eq("match_id", matchId);

      // 👉 On remonte team_id + team(players)
      const { data: matchData, error: matchError } = await supabase
        .from("matches")
        .select(
          "opponent, date, location, players, score, victory, is_live_finished, team_id, team:teams (id, team_name, players, user_id)"
        )
        .eq("id", matchId)
        .single();

      if (matchError || !matchData) return;

      const rawTeam = (matchData as any).team ?? (matchData as any).teams ?? null;
      const teamObj = Array.isArray(rawTeam) ? (rawTeam[0] ?? null) : rawTeam;

      if (teamObj && typeof teamObj.players === "string") {
        try {
          teamObj.players = JSON.parse(teamObj.players);
        } catch {
          /* ignore */
        }
      }

      const normalized: MatchInfo = {
        opponent: matchData.opponent,
        date: matchData.date,
        location: matchData.location,
        players: (matchData.players ?? []) as Player[],
        score: matchData.score ?? undefined,
        victory: matchData.victory ?? undefined,
        is_live_finished: matchData.is_live_finished ?? undefined,
        team_id: matchData.team_id ?? null,
        team: teamObj
          ? {
            id: teamObj.id,
            team_name: teamObj.team_name,
            players: (teamObj.players ?? []) as Player[],
          }
          : null,
      };

      setMatchInfo(normalized);
      setTeamPlayers((normalized.team?.players ?? []) as Player[]);
      setStats((matchStats ?? []) as PlayerStat[]);

      // Owner ?
      setIsOwner(Boolean(teamObj && teamObj.user_id === user.id));

      setLoading(false);

      const { data: shotData } = await supabase
        .from("shot_locations")
        .select("*")
        .eq("match_id", matchId);
      if (shotData) setShots(shotData);
    };
    fetchData();
  }, [matchId, navigate]);

  // ==== tri / helpers stats ====
  const getEval = (s: PlayerStat) =>
    s.points +
    s.rebounds +
    s.assists +
    s.steals +
    s.blocks -
    s.turnovers +
    s.shots_made -
    s.shots_attempted;

  const getPct = (made: number, attempted: number) =>
    attempted > 0 ? ((made / attempted) * 100).toFixed(1) + "%" : "0%";

  // 👉 Séparation équipe / adversaires
  const teamStats = useMemo(
    () => stats.filter((s) => !s.is_opponent),
    [stats]
  );
  // ❗ On n’affiche QUE la ligne globale adverse = player_number = 0
  const opponentStats = useMemo(
    () => stats.filter((s) => s.is_opponent && s.player_number === 0),
    [stats]
  );


  const totalStats = teamStats.reduce(
    (acc, s) => ({
      points: acc.points + s.points,
      assists: acc.assists + s.assists,
      rebounds: acc.rebounds + s.rebounds,
      steals: acc.steals + s.steals,
      blocks: acc.blocks + s.blocks,
      turnovers: acc.turnovers + s.turnovers,
      shots_made: acc.shots_made + s.shots_made,
      shots_attempted: acc.shots_attempted + s.shots_attempted,
      ft_made: acc.ft_made + s.ft_made,
      ft_attempted: acc.ft_attempted + s.ft_attempted,
      three_made: acc.three_made + s.three_made,
      three_attempted: acc.three_attempted + s.three_attempted,
      personal_fouls: acc.personal_fouls + s.personal_fouls,
    }),
    {
      points: 0,
      assists: 0,
      rebounds: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      shots_made: 0,
      shots_attempted: 0,
      ft_made: 0,
      ft_attempted: 0,
      three_made: 0,
      three_attempted: 0,
      personal_fouls: 0,
    }
  );

  const teamEFF = teamStats.reduce((sum, s) => sum + calculateEFF(s), 0);

  const sortedStats = [...teamStats].sort((a, b) => {
    if (!sortKey) return 0;
    const aValue = sortKey === "eval" ? getEval(a) : (a[sortKey] as number);
    const bValue = sortKey === "eval" ? getEval(b) : (b[sortKey] as number);
    return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
  });

  // Tri simple des adversaires (par points desc)
  const sortedOpponentStats = [...opponentStats].sort(
    (a, b) => b.points - a.points
  );

  // Totaux adversaires (pour ligne "Équipe adverse")
  const totalOpponentStats = opponentStats.reduce(
    (acc, s) => ({
      points: acc.points + s.points,
      assists: acc.assists + s.assists,
      rebounds: acc.rebounds + s.rebounds,
      steals: acc.steals + s.steals,
      blocks: acc.blocks + s.blocks,
      turnovers: acc.turnovers + s.turnovers,
      shots_made: acc.shots_made + s.shots_made,
      shots_attempted: acc.shots_attempted + s.shots_attempted,
      ft_made: acc.ft_made + s.ft_made,
      ft_attempted: acc.ft_attempted + s.ft_attempted,
      three_made: acc.three_made + s.three_made,
      three_attempted: acc.three_attempted + s.three_attempted,
      personal_fouls: acc.personal_fouls + s.personal_fouls,
    }),
    {
      points: 0,
      assists: 0,
      rebounds: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      shots_made: 0,
      shots_attempted: 0,
      ft_made: 0,
      ft_attempted: 0,
      three_made: 0,
      three_attempted: 0,
      personal_fouls: 0,
    }
  );

  const handleSort = (key: keyof PlayerStat | "eval") => {
    if (sortKey === key) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortOrder("desc");
    }
  };

  // ==== UI helpers ====
  const locale = lng === "fr" ? "fr-FR" : "en-US";

  /* =========================
     PDF: Convocation
     ========================= */
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const loc = lng === "fr" ? "fr-FR" : "en-US";
    return d.toLocaleDateString(loc, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const loc = lng === "fr" ? "fr-FR" : "en-US";
    return d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
  };
  const loadImageAsDataURL = (src: string) =>
    new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas 2D context not available"));
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = src;
    });
  const openConvocationModal = () => {
    let savedGender = "F";
    try {
      savedGender = localStorage.getItem(CONVOCATION_GENDER_KEY) || "F";
    } catch {
      // ignore (mode privé, storage désactivé, etc.)
    }
    setConvocationForm({
      drivers: "",
      matchPlace: "",
      meetingTime: "",
      meetingPlace: "",
      gender: savedGender,
    });
    setExtraPlayers([]);
    setExtraPlayerFirstName("");
    setExtraPlayerLastName("");
    setIsConvocationModalOpen(true);
  };

  const closeConvocationModal = () => {
    setIsConvocationModalOpen(false);
  };

  const addExtraPlayer = () => {
    const firstName = extraPlayerFirstName.trim();
    const lastName = extraPlayerLastName.trim();
    if (!firstName && !lastName) return;
    setExtraPlayers((prev) => [...prev, { firstName, lastName }]);
    setExtraPlayerFirstName("");
    setExtraPlayerLastName("");
  };

  const removeExtraPlayer = (index: number) => {
    setExtraPlayers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConvocationFieldChange = (
    field: keyof ConvocationForm,
    value: string
  ) => {
    setConvocationForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConvocationGenderChange = (gender: string) => {
    setConvocationForm((prev) => ({ ...prev, gender }));
    try {
      localStorage.setItem(CONVOCATION_GENDER_KEY, gender);
    } catch {
      // ignore (mode privé, storage désactivé, etc.)
    }
  };

  const exportConvocationPDF = async () => {
    if (!matchInfo) return;

    const players: { firstName: string; lastName: string }[] = [
      ...(matchInfo.players ?? []).map((p) => ({
        firstName: p.firstName,
        lastName: p.lastName,
      })),
      ...extraPlayers,
    ];
    if (players.length === 0) {
      alert(
        lng === "fr"
          ? "Aucun(e) joueur(se) sélectionné(e) pour ce match."
          : "No selected players for this game."
      );
      return;
    }

    const L = (fr: string, en: string) => (lng === "fr" ? fr : en);

    const title = L("Convocation de match", "Game Call-Up");
    const opponentLbl = L("Adversaire", "Opponent");
    const dateLbl = L("Jour", "Date");
    const timeLbl = L("Horaire du match", "Game time");
    const homeTxt = L("Domicile", "Home");
    const awayTxt = L("Extérieur", "Away");
    const playersLbl =
      convocationForm.gender === "M"
        ? L("Joueurs sélectionnés", "Selected players")
        : L("Joueuses sélectionnées", "Selected players");

    const meetingTimeLbl = L("Heure du rendez-vous", "Meeting time");
    const meetingPlaceLbl = L("Lieu du rendez-vous", "Meeting place");
    const driversLbl = L("Personnes de voiture", "Drivers / carpool");
    const matchPlaceLbl = L("Lieu du match", "Game location");

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    let y = 20;

    try {
      const logoData = await loadImageAsDataURL(Logo as unknown as string);
      const lw = 120;
      doc.addImage(logoData, "PNG", 40, y - 6, lw, lw * 0.9);
    } catch {
      // logo non bloquant
    }

    const teamName = matchInfo.team?.team_name || "";
    const titleFull = teamName ? `${title} – ${teamName}` : title;

    const titleX = 180;
    const titleMaxWidth = doc.internal.pageSize.getWidth() - 40 - titleX;

    doc.setFont("helvetica", "bold");
    let titleFontSize = 22;
    doc.setFontSize(titleFontSize);
    while (
      titleFontSize > 12 &&
      doc.getTextWidth(titleFull) > titleMaxWidth
    ) {
      titleFontSize -= 1;
      doc.setFontSize(titleFontSize);
    }

    let titleExtraLines = 0;
    if (doc.getTextWidth(titleFull) > titleMaxWidth) {
      const titleLines: string[] = doc.splitTextToSize(
        titleFull,
        titleMaxWidth
      );
      doc.text(titleLines, titleX, y + 22);
      titleExtraLines = titleLines.length - 1;
    } else {
      doc.text(titleFull, titleX, y + 22);
    }
    const titleLineHeight = titleFontSize * 1.15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(
      `${opponentLbl}: ${matchInfo.opponent}`,
      180,
      y + 42 + titleExtraLines * titleLineHeight
    );

    const dateFull = formatDate(matchInfo.date);
    const timeFull = formatTime(matchInfo.date);
    const locStr =
      matchInfo.location === "domicile"
        ? homeTxt
        : matchInfo.location === "extérieur"
          ? awayTxt
          : "-";

    y += 100 + titleExtraLines * titleLineHeight;
    doc.setFontSize(13);
    const lineGap = 18;

    doc.text(`${dateLbl}: ${dateFull}`, 40, y);
    y += lineGap;

    doc.text(`${timeLbl}: ${timeFull}`, 40, y);
    y += lineGap;

    doc.text(
      `${matchPlaceLbl}: ${convocationForm.matchPlace?.trim() || locStr}`,
      40,
      y
    );
    y += lineGap;

    doc.text(
      `${meetingTimeLbl}: ${convocationForm.meetingTime?.trim() || "-"}`,
      40,
      y
    );
    y += lineGap;

    doc.text(
      `${meetingPlaceLbl}: ${convocationForm.meetingPlace?.trim() || "-"}`,
      40,
      y
    );

    if (convocationForm.drivers.trim()) {
      y += lineGap;
      const driversText = `${driversLbl}: ${convocationForm.drivers.trim()}`;
      const wrappedDrivers = doc.splitTextToSize(driversText, 515);
      doc.text(wrappedDrivers, 40, y);
      y += wrappedDrivers.length * 14;
    }

    y += 24;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(playersLbl, 40, y);
    y += 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(L("Nom", "Name"), 40, y);

    y += 6;
    doc.setLineWidth(0.6);
    doc.line(40, y, 555, y);
    y += 12;

    doc.setFont("helvetica", "normal");
    const rowGap = 18;

    players
      .slice()
      .sort((a, b) =>
        `${a.lastName}${a.firstName}`.localeCompare(
          `${b.lastName}${b.firstName}`
        )
      )
      .forEach((p) => {
        if (y > 780) {
          doc.addPage();
          y = 60;
        }
        doc.text(`${p.firstName} ${p.lastName}`, 40, y);
        y += rowGap;
      });

    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(
      L("Document généré avec SwishData", "Document generated with SwishData"),
      40,
      820
    );

    const safeOpponent = matchInfo.opponent.replace(/[^\w-]+/g, "_");
    const fname = `${L("Convocation", "CallUp")}_${safeOpponent}.pdf`;

    if (!Capacitor.isNativePlatform()) {
      doc.save(fname);
      setIsConvocationModalOpen(false);
      return;
    }

    const pdfArrayBuffer = doc.output("arraybuffer") as ArrayBuffer;
    const base64 = arrayBufferToBase64(pdfArrayBuffer);

    const path = `convocations/${fname}`;
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });

    const { uri } = await Filesystem.getUri({
      directory: Directory.Cache,
      path,
    });

    try {
      await FileOpener.open(uri, "application/pdf");
      setIsConvocationModalOpen(false);
    } catch (e) {
      console.error("FileOpener error:", e, uri);
      alert(
        lng === "fr"
          ? "Impossible d’ouvrir le PDF. Installe un lecteur PDF (Google Drive / Adobe Reader) ou réessaie."
          : "Unable to open PDF. Install a PDF viewer (Google Drive / Adobe Reader) or try again."
      );
    }
  };

  // ====== Edition des convoqués (UI + actions) ======
  const openEditPlayers = () => {
    if (!matchInfo) return;
    const current = (matchInfo.players ?? []) as Player[];
    setSelectedNumbers(current.map((p) => p.number));
    setSearch("");
    setIsEditingPlayers(true);
  };

  const filteredTeamPlayers = useMemo(() => {
    if (!search.trim()) return teamPlayers;
    const q = search.toLowerCase();
    return teamPlayers.filter(
      (p) =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        `${p.lastName} ${p.firstName}`.toLowerCase().includes(q) ||
        String(p.number).includes(q)
    );
  }, [teamPlayers, search]);

  const togglePlayer = (num: number) => {
    setSelectedNumbers((prev) =>
      prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]
    );
  };
  const selectAll = () => setSelectedNumbers(teamPlayers.map((p) => p.number));
  const deselectAll = () => setSelectedNumbers([]);

  const savePlayers = async () => {
    if (!matchId) return;
    // snapshot depuis la source équipe
    const invitedPlayers: Player[] = teamPlayers
      .filter((p) => selectedNumbers.includes(p.number))
      .map((p) => ({ ...p }));

    const { error } = await supabase
      .from("matches")
      .update({ players: invitedPlayers })
      .eq("id", matchId);

    if (error) {
      alert(
        lng === "fr"
          ? "Erreur lors de la mise à jour des convoqués."
          : "Error updating selected players."
      );
      return;
    }
    // MAJ local state
    setMatchInfo((prev) =>
      prev ? { ...prev, players: invitedPlayers } : prev
    );
    setIsEditingPlayers(false);
  };

  if (loading)
    return (
      <LogoLoader />
    );
  if (!matchInfo)
    return (
      <p className="text-center text-gray-600 dark:text-gray-300">
        {t("matchdetails:not_found")}
      </p>
    );

  return (
    <section className="p-4 sm:p-6 max-w-6xl mx-auto min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      {/* Header app sticky */}
      <div className="sticky top-0 z-50 -mx-4 sm:-mx-6 mb-4 bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-3">
          {/* Bouton retour */}
          <BackButton />
          {/* Titre centré (court) */}
          <h1 className="safe-title text-center text-3xl font-extrabold text-orange-500">
            {t("matchdetails:page_title", { defaultValue: "Détails du match" })}
          </h1>
        </div>
      </div>

      {/* HEADER CARD */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-5 bg-gradient-to-r from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                {t("matchdetails:title", { opponent: matchInfo.opponent })}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <span>
                  {t("matchdetails:date", {
                    date: new Date(matchInfo.date).toLocaleDateString(locale),
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <AlarmClock className="w-3.5 h-3.5" />
                  {formatTime(matchInfo.date)}
                </span>
                {matchInfo.location && <>• </>}
                {matchInfo.location && (
                  <>
                    {matchInfo.location === "domicile" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        <Home className="w-3.5 h-3.5" />
                        {t("matchdetails:home_label")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                        <Bus className="w-3.5 h-3.5" />
                        {t("matchdetails:away_label")}
                      </span>
                    )}
                  </>
                )}
                {typeof matchInfo.victory === "boolean" && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                    ${matchInfo.victory
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                      }`}
                  >
                    {matchInfo.victory ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                    {matchInfo.victory
                      ? t("matchdetails:result.win")
                      : t("matchdetails:result.loss")}
                  </span>
                )}
                {matchInfo.score && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    {matchInfo.score}
                  </span>
                )}
              </div>
            </div>

            {/* Primary action */}
            <div className="flex flex-wrap items-center gap-2">
              {isOwner && (
                <button
                  onClick={() => navigate(`/modmatch/${matchId}`)}
                  className="inline-flex items-center gap-2 rounded-xl border border-orange-300/60 bg-orange-500 text-white hover:bg-orange-600 active:scale-[0.99] px-4 py-2 font-semibold transition"
                >
                  <Pencil className="w-4 h-4" /> {editMatchLabel}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ACTION BAR */}
        {isOwner && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-800">
            <div className="flex flex-wrap justify-center gap-2">
              {/* Rappel push aux joueurs (match à venir uniquement) */}
              {matchId && matchInfo.date && new Date(matchInfo.date) >= new Date() && (
                <SendReminderButton type="match" eventId={matchId} />
              )}

              {/* Edit convocations */}
              {teamPlayers.length > 0 && (
                <button
                  onClick={openEditPlayers}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 font-medium"
                >
                  <Users className="w-4 h-4" />
                  {lng === "fr" ? "Modifier les convoqués" : "Edit selected"}
                </button>
              )}

              <button
                onClick={() => navigate(`/rotations/${matchId}`)}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 font-medium"
              >
                <ArrowLeftRight className="w-4 h-4" />{" "}
                {t("matchdetails:actions.rotations")}
              </button>

              {/* export convocation */}
              {matchInfo.players && matchInfo.players.length > 0 && (
                <button
                  onClick={openConvocationModal}
                  className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 font-medium"
                  title={
                    lng === "fr"
                      ? "Exporter la convocation (PDF)"
                      : "Export call-up (PDF)"
                  }
                >
                  <FileDown className="w-4 h-4" />
                  {lng === "fr" ? "Convocation (PDF)" : "Call-Up (PDF)"}
                </button>
              )}

              {(!matchInfo.players ||
                matchInfo.players.length === 0 ||
                !matchInfo.score) && (
                  <button
                    onClick={() => navigate(`/editmatch/${matchId}`)}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 font-medium"
                  >
                    <Pencil className="w-4 h-4" />{" "}
                    {t("matchdetails:actions.assign_result")}
                  </button>
                )}

              {stats.length === 0 && (
                <>
                  <button
                    onClick={() => navigate(`/matchstats?matchId=${matchId}`)}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 font-medium"
                  >
                    <PlusCircle className="w-4 h-4" />{" "}
                    {t("matchdetails:actions.add_stats")}
                  </button>
                  <button
                    onClick={() => navigate(`/livestats/${matchId}`)}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-3 py-2 font-medium"
                  >
                    <AlarmClock className="w-4 h-4" />{" "}
                    {t("matchdetails:actions.start_live")}
                  </button>
                </>
              )}

              {stats.length > 0 && matchInfo.is_live_finished === false && (
                <button
                  onClick={() => navigate(`/livestats/${matchId}`)}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-3 py-2 font-medium"
                >
                  <Play className="w-4 h-4" />{" "}
                  {t("matchdetails:actions.resume_live")}
                </button>
              )}

              {stats.length > 0 && (
                <>
                  <button
                    onClick={() => navigate(`/editstats/${matchId}`)}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 font-medium"
                  >
                    <Pencil className="w-4 h-4" />{" "}
                    {t("matchdetails:actions.edit_stats")}
                  </button>
                  <button
                    onClick={() =>
                      navigate(`/matchstats?matchId=${matchId}&editShots=true`)
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 font-medium"
                  >
                    <Dribbble className="w-4 h-4" />{" "}
                    {t("matchdetails:actions.edit_shots")}
                  </button>
                </>
              )}

              <button
                onClick={async () => {
                  if (
                    !matchId ||
                    !window.confirm(t("matchdetails:confirm_delete"))
                  )
                    return;
                  await supabase
                    .from("shot_locations")
                    .delete()
                    .eq("match_id", matchId);
                  await supabase
                    .from("match_stats")
                    .delete()
                    .eq("match_id", matchId);
                  await supabase.from("matches").delete().eq("id", matchId);
                  alert(t("matchdetails:delete_success"));
                  navigate("/allmatchs");
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 font-semibold"
              >
                <Trash2 className="w-4 h-4" />{" "}
                {t("matchdetails:actions.delete_match")}
              </button>
            </div>
          </div>
        )}

        {/* ====== PANNEAU EDIT CONVOQUES ====== */}
        {isOwner && isEditingPlayers && (
          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="inline-flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">
                    {lng === "fr" ? "Convoqués" : "Selected players"}:{" "}
                    {selectedNumbers.length}/{teamPlayers.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <CheckSquare className="w-4 h-4" />
                    {lng === "fr" ? "Tout sélectionner" : "Select all"}
                  </button>
                  <button
                    type="button"
                    onClick={deselectAll}
                    className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <Square className="w-4 h-4" />
                    {lng === "fr" ? "Tout désélectionner" : "Clear"}
                  </button>
                  <div className="relative">
                    <SearchIcon className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={
                        lng === "fr"
                          ? "Rechercher (nom/numéro)"
                          : "Search (name/number)"
                      }
                      className="w-60 pl-7 pr-3 py-2 rounded-lg border bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4 max-h-[420px] overflow-auto pr-1">
                {filteredTeamPlayers.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {lng === "fr" ? "Aucun joueur" : "No players"}
                  </div>
                )}
                {filteredTeamPlayers.map((p) => {
                  const checked = selectedNumbers.includes(p.number);
                  return (
                    <label
                      key={p.number}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${checked
                        ? "bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-900/30"
                        : "bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePlayer(p.number)}
                        className="h-4 w-4 accent-orange-500"
                      />
                                            <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {p.firstName} {p.lastName}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">#{p.number}</div>
                    </label>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  onClick={() => setIsEditingPlayers(false)}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 font-medium dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <XCircle className="w-4 h-4" />
                  {lng === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  onClick={savePlayers}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 font-semibold"
                >
                  <Save className="w-4 h-4" />
                  {lng === "fr" ? "Enregistrer" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
        {isConvocationModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {lng === "fr"
                    ? "Préparer la convocation"
                    : "Prepare call-up"}
                </h2>
                <button
                  onClick={closeConvocationModal}
                  className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <XCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr" ? "Accord du PDF" : "PDF wording"}
                  </label>
                  <select
                    value={convocationForm.gender}
                    onChange={(e) => handleConvocationGenderChange(e.target.value)}
                    className="w-full rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  >
                    <option value="F">{lng === "fr" ? "Féminin (Joueuses)" : "Feminine"}</option>
                    <option value="M">{lng === "fr" ? "Masculin (Joueurs)" : "Masculine"}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {lng === "fr"
                      ? "Choix mémorisé pour les prochaines convocations."
                      : "Remembered for the next call-ups."}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr"
                      ? "Lieu du match"
                      : "Game location"}
                  </label>
                  <input
                    type="text"
                    value={convocationForm.matchPlace}
                    onChange={(e) =>
                      handleConvocationFieldChange("matchPlace", e.target.value)
                    }
                    placeholder={
                      lng === "fr"
                        ? "Ex : Gymnase Jean Moulin"
                        : "Ex: Jean Moulin Gym"
                    }
                    className="w-full rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr"
                      ? "Heure du rendez-vous"
                      : "Meeting time"}
                  </label>
                  <input
                    type="text"
                    value={convocationForm.meetingTime}
                    onChange={(e) =>
                      handleConvocationFieldChange("meetingTime", e.target.value)
                    }
                    placeholder={lng === "fr" ? "Ex : 13h15" : "Ex: 1:15 PM"}
                    className="w-full rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr"
                      ? "Lieu du rendez-vous d’avant-match"
                      : "Pre-game meeting place"}
                  </label>
                  <input
                    type="text"
                    value={convocationForm.meetingPlace}
                    onChange={(e) =>
                      handleConvocationFieldChange("meetingPlace", e.target.value)
                    }
                    placeholder={
                      lng === "fr"
                        ? "Ex : Parking salle municipale"
                        : "Ex: City hall gym parking"
                    }
                    className="w-full rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr"
                      ? "Personnes de voiture (optionnel)"
                      : "Drivers / carpool (optional)"}
                  </label>
                  <textarea
                    value={convocationForm.drivers}
                    onChange={(e) =>
                      handleConvocationFieldChange("drivers", e.target.value)
                    }
                    rows={3}
                    placeholder={
                      lng === "fr"
                        ? "Ex : Julie (3 places), Emma (4 places)"
                        : "Ex: Julie (3 seats), Emma (4 seats)"
                    }
                    className="w-full rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {lng === "fr"
                      ? "Ajouter un(e) joueur(se) hors effectif"
                      : "Add a player not on the roster"}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={extraPlayerFirstName}
                      onChange={(e) => setExtraPlayerFirstName(e.target.value)}
                      placeholder={lng === "fr" ? "Prénom" : "First name"}
                      className="flex-1 rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      value={extraPlayerLastName}
                      onChange={(e) => setExtraPlayerLastName(e.target.value)}
                      placeholder={lng === "fr" ? "Nom" : "Last name"}
                      className="flex-1 rounded-lg border bg-white border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={addExtraPlayer}
                      disabled={
                        !extraPlayerFirstName.trim() &&
                        !extraPlayerLastName.trim()
                      }
                      className="inline-flex items-center justify-center gap-1 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 font-medium dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusCircle className="w-4 h-4" />
                      {lng === "fr" ? "Ajouter" : "Add"}
                    </button>
                  </div>

                  {extraPlayers.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {extraPlayers.map((p, index) => (
                        <li
                          key={`${p.firstName}-${p.lastName}-${index}`}
                          className="flex items-center justify-between rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-800 dark:text-gray-100"
                        >
                          <span>
                            {p.firstName} {p.lastName}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeExtraPlayer(index)}
                            className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                          >
                            <XCircle className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={closeConvocationModal}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 font-medium dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <XCircle className="w-4 h-4" />
                  {lng === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  onClick={exportConvocationPDF}
                  className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 font-semibold"
                >
                  <FileDown className="w-4 h-4" />
                  {lng === "fr" ? "Générer le PDF" : "Generate PDF"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* TABS */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="w-full max-w-xl mx-auto">
            <div className="w-full flex justify-center">
              <div
                role="tablist"
                aria-label="Match tabs"
                className="relative inline-grid grid-cols-2 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900"
                style={{ minWidth: 260 }}
              >
                {/* Indicateur orange animé */}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute bottom-0 left-0 h-0.5 w-1/2 bg-orange-500 transition-transform duration-300 ease-out ${activeTab === "shots" ? "translate-x-full" : "translate-x-0"
                    }`}
                />

                <button
                  role="tab"
                  aria-selected={activeTab === "stats"}
                  onClick={() => setActiveTab("stats")}
                  className={`px-5 py-2 text-sm font-medium flex items-center justify-center gap-2 transition ${activeTab === "stats"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                >
                  <BarChart2 className="w-4 h-4" />
                  {t("matchdetails:tabs.stats")}
                </button>

                <button
                  role="tab"
                  aria-selected={activeTab === "shots"}
                  onClick={() => setActiveTab("shots")}
                  className={`px-5 py-2 text-sm font-medium flex items-center justify-center gap-2 transition ${activeTab === "shots"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                >
                  <Dribbble className="w-4 h-4" />
                  {t("matchdetails:tabs.shots")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="px-4 sm:px-6 pb-6">
          {/* SHOTS TAB */}
          {activeTab === "shots" && (
            <div className="mt-5 flex flex-col items-center">
              <label className="mb-2 font-medium text-gray-800 dark:text-gray-200">
                {t("matchdetails:shots.filter_label")}
              </label>
              <select
                className="mb-5 w-full max-w-sm text-center p-2 border rounded-lg bg-white text-gray-900 border-gray-300 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                value={selectedShooter}
                onChange={(e) => setSelectedShooter(e.target.value)}
              >
                <option value="">
                  {t("matchdetails:shots.all_players")}
                </option>
                {matchInfo.players?.map((p) => (
                  <option
                    key={`${p.firstName}-${p.lastName}-${p.number}`}
                    value={p.number}
                  >
                    {p.firstName} {p.lastName}
                  </option>
                ))}
              </select>

              <div className="relative w-full max-w-3xl mx-auto rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800">
                <img
                  src="/court.png"
                  alt={t("matchdetails:shots.court_alt")}
                  className="w-full h-auto select-none"
                />
                {shots
                  .filter(
                    (s) =>
                      selectedShooter === "" ||
                      s.player_number === Number(selectedShooter)
                  )
                  .map((s: any, index: number) => (
                    <div
                      key={index}
                      className="absolute -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${s.x}%`, top: `${s.y}%` }}
                    >
                      {s.made ? (
                        <div className="w-3.5 h-3.5 rounded-full bg-green-500 ring-2 ring-white/80 dark:ring-gray-900/80" />
                      ) : (
                        <div className="w-4 h-4 grid place-items-center">
                          <span className="block text-lg leading-none text-rose-600">
                            ×
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* STATS TAB */}
          {activeTab === "stats" && stats.length > 0 && (
            <div className="mt-5 space-y-8">
              {/* ====== TABLE MON EQUIPE ====== */}
              <div className="overflow-x-auto">
                <h2 className="mb-2 text-base font-semibold">
                  {lng === "fr" ? "Mon équipe" : "My team"}
                </h2>
                <table className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-900">
                  <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                    <tr className="text-gray-900 dark:text-gray-100">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">
                        {t("matchdetails:table.player")}
                      </th>
                      <th className="p-2 text-left">
                        {t("matchdetails:table.pos")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("points")}
                      >
                        {t("matchdetails:table.pts")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("assists")}
                      >
                        {t("matchdetails:table.ast")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("rebounds")}
                      >
                        {t("matchdetails:table.reb")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("steals")}
                      >
                        {t("matchdetails:table.stl")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("blocks")}
                      >
                        {t("matchdetails:table.blk")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("turnovers")}
                      >
                        {t("matchdetails:table.to")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("personal_fouls")}
                      >
                        {t("matchdetails:table.pf")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.fg")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.fg_pct")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.ft")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.ft_pct")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.tp")}
                      </th>
                      <th className="p-2">
                        {t("matchdetails:table.tp_pct")}
                      </th>
                      <th
                        className="p-2 cursor-pointer"
                        onClick={() => handleSort("eval")}
                      >
                        {t("matchdetails:table.eval")}
                      </th>
                      {isPremium && <th className="p-2">TS%</th>}
                      {isPremium && <th className="p-2">eFG%</th>}
                      {isPremium && <th className="p-2">EFF</th>}
                    </tr>
                  </thead>
                  <tbody className="[&>tr]:border-b [&>tr]:border-gray-200 dark:[&>tr]:border-gray-800">
                    {sortedStats.map((s, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        <td className="p-2">{s.player_number}</td>
                        <td className="p-2">{s.player_name}</td>
                        <td className="p-2">{s.position}</td>
                        <td className="p-2">{s.points}</td>
                        <td className="p-2">{s.assists}</td>
                        <td className="p-2">{s.rebounds}</td>
                        <td className="p-2">{s.steals}</td>
                        <td className="p-2">{s.blocks}</td>
                        <td className="p-2">{s.turnovers}</td>
                        <td className="p-2">{s.personal_fouls}</td>
                        <td className="p-2">
                          {s.shots_made}/{s.shots_attempted}
                        </td>
                        <td className="p-2">
                          {getPct(s.shots_made, s.shots_attempted)}
                        </td>
                        <td className="p-2">
                          {s.ft_made}/{s.ft_attempted}
                        </td>
                        <td className="p-2">
                          {getPct(s.ft_made, s.ft_attempted)}
                        </td>
                        <td className="p-2">
                          {s.three_made}/{s.three_attempted}
                        </td>
                        <td className="p-2">
                          {getPct(s.three_made, s.three_attempted)}
                        </td>
                        <td className="p-2">{getEval(s)}</td>
                        {isPremium && (
                          <td className="p-2">
                            {calculateTS(
                              s.points,
                              s.shots_attempted,
                              s.ft_attempted
                            )}
                          </td>
                        )}
                        {isPremium && (
                          <td className="p-2">
                            {calculateEFG(
                              s.shots_made,
                              s.shots_attempted,
                              s.three_made
                            )}
                          </td>
                        )}
                        {isPremium && (
                          <td className="p-2">{calculateEFF(s)}</td>
                        )}
                      </tr>
                    ))}
                    {/* Team row */}
                    <tr className="bg-gray-100 dark:bg-gray-800 font-semibold text-orange-600 dark:text-orange-400">
                      <td className="p-2">–</td>
                      <td className="p-2">
                        {t("matchdetails:table.team")}
                      </td>
                      <td className="p-2">–</td>
                      <td className="p-2">{totalStats.points}</td>
                      <td className="p-2">{totalStats.assists}</td>
                      <td className="p-2">{totalStats.rebounds}</td>
                      <td className="p-2">{totalStats.steals}</td>
                      <td className="p-2">{totalStats.blocks}</td>
                      <td className="p-2">{totalStats.turnovers}</td>
                      <td className="p-2">{totalStats.personal_fouls}</td>
                      <td className="p-2">
                        {totalStats.shots_made}/
                        {totalStats.shots_attempted}
                      </td>
                      <td className="p-2">
                        {getPct(
                          totalStats.shots_made,
                          totalStats.shots_attempted
                        )}
                      </td>
                      <td className="p-2">
                        {totalStats.ft_made}/{totalStats.ft_attempted}
                      </td>
                      <td className="p-2">
                        {getPct(
                          totalStats.ft_made,
                          totalStats.ft_attempted
                        )}
                      </td>
                      <td className="p-2">
                        {totalStats.three_made}/
                        {totalStats.three_attempted}
                      </td>
                      <td className="p-2">
                        {getPct(
                          totalStats.three_made,
                          totalStats.three_attempted
                        )}
                      </td>
                      <td className="p-2">
                        {teamStats.reduce((sum, s) => sum + getEval(s), 0)}
                      </td>
                      {isPremium && (
                        <td className="p-2">{calculateTeamTS(totalStats)}</td>
                      )}
                      {isPremium && (
                        <td className="p-2">{calculateTeamEFG(totalStats)}</td>
                      )}
                      {isPremium && (
                        <td className="p-2">{teamEFF}</td>
                      )}
                    </tr>
                  </tbody>
                </table>

                <div className="mt-4">
                  <ExportButtons
                    data={teamStats}
                    fileName={`match_${matchId}_stats`}
                    excelColumns={{
                      [t("matchdetails:export.excel.number")]:
                        "player_number",
                      [t("matchdetails:export.excel.name")]: "player_name",
                      [t("matchdetails:export.excel.pos")]: "position",
                      [t("matchdetails:export.excel.points")]: "points",
                      [t("matchdetails:export.excel.assists")]: "assists",
                      [t("matchdetails:export.excel.rebounds")]: "rebounds",
                      [t("matchdetails:export.excel.steals")]: "steals",
                      [t("matchdetails:export.excel.blocks")]: "blocks",
                      [t("matchdetails:export.excel.turnovers")]: "turnovers",
                      [t("matchdetails:export.excel.pf")]: "personal_fouls",
                      [t("matchdetails:export.excel.fg_made")]: "shots_made",
                      [t("matchdetails:export.excel.fg_att")]:
                        "shots_attempted",
                      [t("matchdetails:export.excel.ft_made")]: "ft_made",
                      [t("matchdetails:export.excel.ft_att")]:
                        "ft_attempted",
                      [t("matchdetails:export.excel.tp_made")]: "three_made",
                      [t("matchdetails:export.excel.tp_att")]:
                        "three_attempted",
                    }}
                    pdfColumns={[
                      { header: "#", key: "player_number" },
                      {
                        header: t("matchdetails:export.pdf.name"),
                        key: "player_name",
                      },
                      {
                        header: t("matchdetails:export.pdf.pos"),
                        key: "position",
                      },
                      {
                        header: t("matchdetails:export.pdf.pts"),
                        key: "points",
                      },
                      {
                        header: t("matchdetails:export.pdf.ast"),
                        key: "assists",
                      },
                      {
                        header: t("matchdetails:export.pdf.reb"),
                        key: "rebounds",
                      },
                      {
                        header: t("matchdetails:export.pdf.stl"),
                        key: "steals",
                      },
                      {
                        header: t("matchdetails:export.pdf.blk"),
                        key: "blocks",
                      },
                      {
                        header: t("matchdetails:export.pdf.to"),
                        key: "turnovers",
                      },
                      {
                        header: t("matchdetails:export.pdf.pf"),
                        key: "personal_fouls",
                      },
                      {
                        header: t("matchdetails:export.pdf.fg_made"),
                        key: "shots_made",
                      },
                      {
                        header: t("matchdetails:export.pdf.fg_att"),
                        key: "shots_attempted",
                      },
                      {
                        header: t("matchdetails:export.pdf.ft_made"),
                        key: "ft_made",
                      },
                      {
                        header: t("matchdetails:export.pdf.ft_att"),
                        key: "ft_attempted",
                      },
                      {
                        header: t("matchdetails:export.pdf.tp_made"),
                        key: "three_made",
                      },
                      {
                        header: t("matchdetails:export.pdf.tp_att"),
                        key: "three_attempted",
                      },
                    ]}
                  />
                </div>
              </div>

              {/* ====== TABLE ADVERSAIRES ====== */}
              {opponentStats.length > 0 && (
                <div className="overflow-x-auto">
                  <h2 className="mb-2 text-base font-semibold">
                    {lng === "fr" ? "Adversaires" : "Opponents"}
                  </h2>
                  <table className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-900">
                    <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                      <tr className="text-gray-900 dark:text-gray-100">
                        <th className="p-2 text-left">#</th>
                        <th className="p-2 text-left">
                          {t("matchdetails:table.player")}
                        </th>
                        <th className="p-2 text-left">
                          {t("matchdetails:table.pos")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.pts")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.ast")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.reb")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.stl")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.blk")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.to")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.pf")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.fg")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.ft")}
                        </th>
                        <th className="p-2">
                          {t("matchdetails:table.tp")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&>tr]:border-b [&>tr]:border-gray-200 dark:[&>tr]:border-gray-800">
                      {sortedOpponentStats.map((s, index) => {
                        const isTeamLine = s.player_number === 0;
                        const displayNumber = isTeamLine ? "–" : s.player_number;
                        const displayName = isTeamLine
                          ? lng === "fr"
                            ? "Équipe adverse"
                            : "Opponent team"
                          : s.player_name;

                        return (
                          <tr
                            key={index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/60"
                          >
                            <td className="p-2">{displayNumber}</td>
                            <td className="p-2">{displayName}</td>
                            <td className="p-2">
                              {isTeamLine ? "–" : s.position}
                            </td>
                            <td className="p-2">{s.points}</td>
                            <td className="p-2">{s.assists}</td>
                            <td className="p-2">{s.rebounds}</td>
                            <td className="p-2">{s.steals}</td>
                            <td className="p-2">{s.blocks}</td>
                            <td className="p-2">{s.turnovers}</td>
                            <td className="p-2">{s.personal_fouls}</td>
                            <td className="p-2">
                              {s.shots_made}/{s.shots_attempted}
                            </td>
                            <td className="p-2">
                              {s.ft_made}/{s.ft_attempted}
                            </td>
                            <td className="p-2">
                              {s.three_made}/{s.three_attempted}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Ligne totaux adversaire */}
                      <tr className="bg-gray-100 dark:bg-gray-800 font-semibold text-orange-600 dark:text-orange-400">
                        <td className="p-2">–</td>
                        <td className="p-2">
                          {lng === "fr" ? "Équipe adverse" : "Opponent team"}
                        </td>
                        <td className="p-2">–</td>
                        <td className="p-2">{totalOpponentStats.points}</td>
                        <td className="p-2">{totalOpponentStats.assists}</td>
                        <td className="p-2">{totalOpponentStats.rebounds}</td>
                        <td className="p-2">{totalOpponentStats.steals}</td>
                        <td className="p-2">{totalOpponentStats.blocks}</td>
                        <td className="p-2">{totalOpponentStats.turnovers}</td>
                        <td className="p-2">
                          {totalOpponentStats.personal_fouls}
                        </td>
                        <td className="p-2">
                          {totalOpponentStats.shots_made}/
                          {totalOpponentStats.shots_attempted}
                        </td>
                        <td className="p-2">
                          {totalOpponentStats.ft_made}/
                          {totalOpponentStats.ft_attempted}
                        </td>
                        <td className="p-2">
                          {totalOpponentStats.three_made}/
                          {totalOpponentStats.three_attempted}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "stats" && stats.length === 0 && (
            <p className="mt-6 text-center text-gray-600 dark:text-gray-300">
              {lng === "fr"
                ? "Aucune statistique pour ce match pour le moment."
                : "No stats recorded for this game yet."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
